// Handle LLM tool use

import { DEFAULT_CLIENT_FIND_RELATED, DEFAULT_CLIENT_SIM_THRESHOLD } from "./stores/SearchSessionStore";
import { TOOL_RUN_SEARCH } from "./llm/LLMClient";

const RE_POLITE = /^(?:(?:yes, )?please|thank you[^.]*\.|Certainly! *|I apologize, but *) ([a-z])/gi;


export const blunt = s => {
  while (RE_POLITE.test(s)) {
    const text = s.replace(RE_POLITE, "$1");
    s = text.charAt(0).toUpperCase() + text.slice(1);
  }
  return s;
};

// Fields the backend's run_search tool actually accepts: "ids" plus every externally-exposed
// SearchParams field (rd/search plex/search/search_params.py bound_param_names()). The
// run_search_analyst tool-call input we receive here shares this same param surface but also
// carries analyst-only orchestration fields (expand_members, instructions) that run_search
// doesn't know and the backend's /search endpoint rejects outright — allowlist against what
// run_search accepts rather than blacklisting the analyst's extra fields, so a new analyst-only
// field can't leak through the same way again.
const RUN_SEARCH_PARAM_KEYS = new Set([
  "ids", "imap", "sim_type", "sim_threshold", "sim_limit", "sim_method",
  "sim_substructure", "find_related", "indirect", "density_expansion",
  "ranking_metric", "min_expansion", "xf",
]);

// A tool call's `ids` as a list. The model sometimes sends the array JSON-encoded, as a string
// ('["entrez:1","entrez:2"]'). The backend normalizes it before running the tool
// (validate_tool_params in rd plex/search/llm/tools.py), but the conversation keeps the string,
// and spreading a string gives its characters: the search button read "[, ", e, n, t and 17
// others" and linked to a search on them (PLEX-6670). Same recovery as the backend: parse the
// JSON, else the bracketed array at its start (sibling params embedded after it), else a bare id
// is a one-id list.
export const toolIds = ids => {
  if (Array.isArray(ids)) return ids;
  if (typeof ids !== "string" || !ids.trim()) return [];
  const parsedList = text => {
    try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  };
  const bracketed = ids.match(/^\s*(\[[\s\S]*\])/);
  return parsedList(ids) || (bracketed && parsedList(bracketed[1])) || (ids.startsWith("[") ? [] : [ids]);
};

// A run_search_analyst call's own `input` is only the orchestration request (geneset/cpdset
// ids, expand_members flag, instructions) — the real, already-expanded ids/params it actually
// searched on live in its own run_search leaf call, nested in its sub-agent's turns
// (block.agentMessages, attached by Conversation.js). Recurse through agentMessages to find
// that leaf call so the UI's "search again" button can mirror what was actually searched,
// not the pre-expansion request. Returns the first run_search toolUse found, or undefined if
// the analyst never got that far (e.g. errored before dispatching it).
export const findRealRunSearchToolUse = (agentMessages) => {
  for (const turn of (agentMessages || [])) {
    for (const block of (turn?.content || [])) {
      if (block?.toolUse?.name === TOOL_RUN_SEARCH && toolIds(block.toolUse.input?.ids).length) {
        return block.toolUse;
      }
      if (block?.agentMessages?.length) {
        const found = findRealRunSearchToolUse(block.agentMessages);
        if (found) return found;
      }
    }
  }
  return undefined;
};

// Tool inputs saved before the search tools moved to SearchParams' snake_case names (rd
// 236d78947, 2026-08-15) use camelCase keys: simSubstructure, simType, simThreshold, ... Match
// them to the allowlist too, or an older conversation's "search again" button drops every one of
// its parameters and reruns as a default similarity search (PLEX-6538).
const toSnakeCase = key => key.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();

export const extractSearchParams = (input, dsConfig) => {
  const searchParams = Object.fromEntries(
    Object.entries(input || {})
      .map(([key, value]) => [toSnakeCase(key), value])
      .filter(([key]) => RUN_SEARCH_PARAM_KEYS.has(key))
  );
  if ("ids" in searchParams) searchParams.ids = toolIds(searchParams.ids);
  // Override with client default settings
  return {
    simThreshold: DEFAULT_CLIENT_SIM_THRESHOLD,
    findRelated: DEFAULT_CLIENT_FIND_RELATED,
    dsConfig,
    ...searchParams
  };
}
